import React from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { Link } from 'react-router-dom';
import { useTranslate } from '../../mixins/translate/translate';
import { IStoreState } from '../../store/IStoreState';
import { useOpenApiFetch } from '../../store/useOpenApiFetch';
import {
    PanelBody,
    PanelHeading,
    PanelWithoutForm,
    Table,
    TableBody,
    TableData,
    TableEmphasizedText,
    TableHead,
    TableHeadRow,
    TableHeader,
    TableRow,
    TableSubText,
} from '../elements';
import { Spinner } from '../spinners';
import { NullableFormattedDate } from '../utils/NullableFormattedDate';
import { SurveyStatusLabel } from '../utils/SurveyStatusLabel';
import { TagLabels } from '../utils/TagLabels';
import { WorkflowDueDateLabel } from '../utils/workflowDueDateLabel';

export function DashboardAssignedSurveysList(props: { useProductTitle?: boolean | undefined }) {
    const translate = useTranslate();
    const navigate = useNavigate();

    const areLabelsVisibleInOverviews = useSelector((state: IStoreState) => state.authentication.areLabelsVisibleInOverviews);

    const { data: assignedSurveys, isLoading } = useOpenApiFetch('/api/surveys/assigned', 'get', {}, []);

    if (isLoading) {
        return <Spinner />;
    }

    if (assignedSurveys?.items.length === 0) {
        return null;
    }

    return (
        <PanelWithoutForm>
            <PanelHeading
                icon="fa fa-camera"
                text={props.useProductTitle ? translate('dashboard.assignedProductList') : translate('dashboard.assignedSurveyList')}
            />
            <PanelBody noPadding={true}>
                <Table hover={true}>
                    <TableHead>
                        <TableHeadRow light={true}>
                            <TableHeader>{props.useProductTitle ? translate('common.name') : translate('common.survey')}</TableHeader>
                            <TableHeader>{props.useProductTitle ? translate('common.template') : translate('common.inspectionTemplate')}</TableHeader>
                            <TableHeader>{translate('survey.assignmentUpdatedOn')}</TableHeader>
                            <TableHeader>{translate('survey.reportDeadlineOnDate')}</TableHeader>
                            <TableHeader align="right">{translate('common.status')}</TableHeader>
                        </TableHeadRow>
                    </TableHead>

                    <TableBody>
                        {assignedSurveys?.items.map((item) => (
                            <TableRow key={item.id} onClick={() => navigate(`/surveys/${item.id}`)}>
                                <TableData>
                                    <Link to={`/surveys/${item.id}`} className="no-underline">
                                        <div className="tooltipZXY">
                                            <TableEmphasizedText text={item.object.name} />
                                            <TableSubText text={item.description ?? ''} />
                                            {areLabelsVisibleInOverviews && <TagLabels labels={item.labels} />}
                                            <span className="tooltiptext">{item.surveyComment.text}</span>
                                        </div>
                                    </Link>
                                </TableData>
                                <TableData>
                                    {(item.inspectionStandardType && <>{translate(`inspectionStandard.${item.inspectionStandardType}`)} </>) || (
                                        <>NEN 2767</>
                                    )}
                                </TableData>
                                <TableData nowrap={true}>
                                    <NullableFormattedDate value={item.assignmentUpdatedOn} />
                                </TableData>
                                <TableData nowrap={true}>
                                    <WorkflowDueDateLabel value={item.reportDeadlineOnDate} />
                                </TableData>
                                <TableData alignRight={true}>
                                    <SurveyStatusLabel value={item.status} />
                                </TableData>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </PanelBody>
        </PanelWithoutForm>
    );
}
